import { computed, ref, watch } from 'vue'
import { useIntervalFn } from '@vueuse/core'

import type {
  ButtonReloadOptions,
  ButtonReloadReturn,
} from '../types/interfaces'

const SECOND_MS = 1000

const formatTime = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  const mm = minutes.toString().padStart(2, '0')
  const ss = seconds.toString().padStart(2, '0')
  return `${mm}:${ss}`
}

export const useButtonReload = ({
  props,
  onLoading,
}: ButtonReloadOptions): ButtonReloadReturn => {
  const refRemaining = ref<number>(props.seconds)
  const refIsLoading = ref<boolean>(false)

  const time = computed<string>(() => formatTime(refRemaining.value))

  const isDisabled = computed<boolean>(() => !!props.disabled || refIsLoading.value)

  const trigger = async (): Promise<void> => {
    if (refIsLoading.value) return

    pause()
    refRemaining.value = props.seconds
    refIsLoading.value = true
    onLoading(true)

    try {
      if (props.onClick) {
        await props.onClick()
      }
    }
    finally {
      refIsLoading.value = false
      onLoading(false)
      resume()
    }
  }

  const tick = (): void => {
    if (isDisabled.value) return
    if (refRemaining.value <= 1) {
      void trigger()
      return
    }
    refRemaining.value -= 1
  }

  const { pause, resume } = useIntervalFn(tick, SECOND_MS, { immediateCallback: false })

  const handleClick = (): void => {
    if (isDisabled.value) return
    void trigger()
  }

  watch(() => props.seconds, (next) => {
    refRemaining.value = next
  })

  watch(() => props.disabled, (isDisabled) => {
    if (isDisabled) {
      pause()
      refRemaining.value = props.seconds
    }
    else if (!refIsLoading.value) {
      resume()
    }
  })

  return {
    time,
    isDisabled,
    handleClick,
  }
}
